import React, { useEffect, useMemo, useState } from 'react';
import useAppStore from '../../store/useAppStore.js';
import { getOrders } from '../../services/orderApi.js';
import { getWaiters } from '../../services/waiterApi.js';
import { formatCurrencyShort } from '../../utils/amountHelpers.js';
import Pagination from '../common/Pagination.js';
import TabToggle from '../common/TabToggle.js';

const statusTabs = [
  { label: 'All Orders', value: 'all' },
  { label: 'Pending', value: 'pending' },
  { label: 'Completed', value: 'completed' },
  { label: 'Cancelled', value: 'cancelled' }
];

const AdminWaiterReport = () => {
  const { admin } = useAppStore((state) => ({
    admin: state.admin
  }));

  const [orders, setOrders] = useState([]);
  const [waiters, setWaiters] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [selectedBranch, setSelectedBranch] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 10;

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      setError(null);
      try {
        const [orderResponse, waiterResponse] = await Promise.all([getOrders(), getWaiters()]);
        setOrders(orderResponse.orders || []);
        setWaiters(waiterResponse.waiters || []);
      } catch (err) {
        setError(err.message || 'Unable to load waiter report');
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, []);

  const getWaiterName = (waiterId) => {
    if (!waiterId) return 'N/A';
    const waiter = waiters.find((waiter) => String(waiter.id) === String(waiterId));
    return waiter ? waiter.name : `Waiter #${waiterId}`;
  };

  const branchOptions = useMemo(() => [
    { label: 'All Branches', value: 'all' },
    ...(admin.branches || []).map((branch) => ({
      label: branch.name || `Branch ${branch.id}`,
      value: String(branch.id)
    }))
  ], [admin.branches]);

  const filteredOrders = useMemo(() => {
    return (orders || []).filter((order) => {
      if (order.order_type !== 'waiter' && !order.waiter_id) {
        return false;
      }
      if (selectedStatus !== 'all' && order.status !== selectedStatus) {
        return false;
      }
      if (selectedBranch !== 'all' && String(order.branch_id) !== selectedBranch) {
        return false;
      }
      if (!searchQuery.trim()) {
        return true;
      }
      const query = searchQuery.trim().toLowerCase();
      return (
        String(order.id).toLowerCase().includes(query) ||
        String(order.token_number || '').toLowerCase().includes(query) ||
        getWaiterName(order.waiter_id).toLowerCase().includes(query)
      );
    });
  }, [orders, selectedBranch, selectedStatus, searchQuery, waiters]);

  useEffect(() => {
    setCurrentPage(1);
  }, [selectedStatus, selectedBranch, searchQuery]);

  const waiterStats = useMemo(() => {
    const stats = {};
    filteredOrders.forEach((order) => {
      const waiterId = order.waiter_id;
      if (!waiterId) return;

      const key = String(waiterId);
      if (!stats[key]) {
        stats[key] = {
          waiterId: key,
          name: getWaiterName(waiterId),
          totalOrders: 0,
          completed: 0,
          cancelled: 0,
          pending: 0,
          revenue: 0,
          lastOrderAt: null
        };
      }
      const record = stats[key];
      record.totalOrders += 1;
      if (order.status === 'completed') record.completed += 1;
      if (order.status === 'cancelled') record.cancelled += 1;
      if (order.status !== 'completed' && order.status !== 'cancelled') record.pending += 1;
      const amount = Number(order.total || 0);
      record.revenue += Number.isFinite(amount) ? amount : 0;
      const createdAt = order.created_at ? new Date(order.created_at) : null;
      if (createdAt && (!record.lastOrderAt || createdAt > record.lastOrderAt)) {
        record.lastOrderAt = createdAt;
      }
    });

    return Object.values(stats).sort((a, b) => b.totalOrders - a.totalOrders);
  }, [filteredOrders, waiters]);

  const totalWaiterOrders = waiterStats.reduce((sum, item) => sum + item.totalOrders, 0);
  const totalRevenue = waiterStats.reduce((sum, item) => sum + item.revenue, 0);
  const averageOrders = waiterStats.length ? (totalWaiterOrders / waiterStats.length).toFixed(1) : '0.0';
  const topPerformer = waiterStats[0];

  const paginatedStats = useMemo(() => {
    const startIndex = (currentPage - 1) * pageSize;
    return waiterStats.slice(startIndex, startIndex + pageSize);
  }, [waiterStats, currentPage, pageSize]);

  const formatCurrency = formatCurrencyShort;

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
        <div>
          <h1 className="text-4xl font-bold text-slate-900">Waiter Report</h1>
          <p className="mt-2 text-gray-600 max-w-2xl">
            Monitor waiter performance, order completion rates, and branch delivery results.
          </p>
        </div>
      </div>

      {error && (
        <div className="rounded-3xl border border-red-200 bg-red-50 p-4 text-sm text-red-700">
          {error}
        </div>
      )}

      <div className="grid gap-4 md:grid-cols-4">
        <div className="rounded-3xl border border-gray-200 bg-white p-6 shadow-sm">
          <div className="text-sm text-gray-500">Waiters tracked</div>
          <div className="mt-2 text-3xl font-semibold text-slate-900">{waiterStats.length}</div>
        </div>
        <div className="rounded-3xl border border-gray-200 bg-white p-6 shadow-sm">
          <div className="text-sm text-gray-500">Total waiter orders</div>
          <div className="mt-2 text-3xl font-semibold text-slate-900">{totalWaiterOrders}</div>
        </div>
        <div className="rounded-3xl border border-gray-200 bg-white p-6 shadow-sm">
          <div className="text-sm text-gray-500">Revenue from waiter orders</div>
          <div className="mt-2 text-3xl font-semibold text-emerald-700">{formatCurrency(totalRevenue)}</div>
        </div>
        <div className="rounded-3xl border border-gray-200 bg-white p-6 shadow-sm">
          <div className="text-sm text-gray-500">Average orders per waiter</div>
          <div className="mt-2 text-3xl font-semibold text-slate-900">{averageOrders}</div>
        </div>
      </div>

      <div className="rounded-3xl border border-gray-200 bg-white p-6 shadow-sm">
        <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
          <TabToggle options={statusTabs} activeValue={selectedStatus} onChange={setSelectedStatus} />
          <div className="flex flex-wrap gap-3 items-center">
            <div className="min-w-[200px]">
              <select
                value={selectedBranch}
                onChange={(e) => setSelectedBranch(e.target.value)}
                className="w-full rounded-3xl border border-gray-300 bg-white px-4 py-3 text-sm text-gray-900 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200"
              >
                {branchOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search waiter name, order ID, token"
              className="w-full min-w-[240px] rounded-3xl border border-gray-300 bg-white px-4 py-3 text-sm text-gray-900 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200"
            />
          </div>
        </div>
      </div>

      <div className="rounded-3xl border border-gray-200 bg-white p-6 shadow-sm">
        <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
          <div>
            <h2 className="text-2xl font-semibold text-slate-900">Waiter performance</h2>
            <p className="mt-1 text-sm text-gray-500">Ranked by waiter orders and completion performance.</p>
          </div>
          {topPerformer && (
            <div className="rounded-3xl bg-slate-50 p-4 text-sm text-gray-700">
              Top performer: <span className="font-semibold text-slate-900">{topPerformer.name}</span> with {topPerformer.totalOrders} orders
            </div>
          )}
        </div>

        <div className="mt-6 overflow-auto h-[400px]">
          <table className="min-w-full divide-y divide-gray-200 text-sm text-gray-700">
            <thead className="bg-gray-50 text-left text-gray-600">
              <tr>
                <th className="px-4 py-3 font-medium">Waiter</th>
                <th className="px-4 py-3 font-medium">Orders</th>
                <th className="px-4 py-3 font-medium">Completed</th>
                <th className="px-4 py-3 font-medium">Cancelled</th>
                <th className="px-4 py-3 font-medium">Pending</th>
                <th className="px-4 py-3 font-medium">Revenue</th>
                <th className="px-4 py-3 font-medium">Avg order</th>
                <th className="px-4 py-3 font-medium">Last order</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 bg-white">
              {loading ? (
                <tr>
                  <td colSpan="8" className="px-4 py-10 text-center text-gray-500">
                    Loading waiter performance...
                  </td>
                </tr>
              ) : waiterStats.length === 0 ? (
                <tr>
                  <td colSpan="8" className="px-4 py-10 text-center text-gray-500">
                    No waiter performance data available for the current filters.
                  </td>
                </tr>
              ) : (
                paginatedStats.map((waiter) => (
                  <tr key={waiter.waiterId} className="hover:bg-gray-50 transition">
                    <td className="px-4 py-4 font-medium text-slate-900">{waiter.name}</td>
                    <td className="px-4 py-4">{waiter.totalOrders}</td>
                    <td className="px-4 py-4 text-emerald-700">{waiter.completed}</td>
                    <td className="px-4 py-4 text-rose-700">{waiter.cancelled}</td>
                    <td className="px-4 py-4 text-yellow-700">{waiter.pending}</td>
                    <td className="px-4 py-4 font-semibold text-slate-900">{formatCurrency(waiter.revenue)}</td>
                    <td className="px-4 py-4 text-gray-600">{waiter.totalOrders ? formatCurrency(waiter.revenue / waiter.totalOrders) : formatCurrency(0)}</td>
                    <td className="px-4 py-4 text-gray-600">{waiter.lastOrderAt ? waiter.lastOrderAt.toLocaleString() : '-'}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        {waiterStats.length > 0 && (
          <Pagination
            currentPage={currentPage}
            totalItems={waiterStats.length}
            pageSize={pageSize}
            onPageChange={setCurrentPage}
          />
        )}
      </div>
    </div>
  );
};

export default AdminWaiterReport;
